import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, ShieldCheck, ArrowLeft, Send, Info } from "lucide-react";

/**
 * Help · Feedback · Share Your VAULT Experience
 *
 * Customer submission form for the testimonial system. Never forces a
 * positive answer; explicit publication permission is captured before
 * anything can be published. Nothing is public until an admin verifies
 * + approves + publishes it.
 */

const DISPLAY_FORMATS = [
    { key: "full",            label: "Full name",             example: "Pradeep Daga" },
    { key: "first_initial",   label: "First name + initial",  example: "Pradeep D." },
    { key: "first",           label: "First name only",       example: "Pradeep" },
    { key: "anonymous_role",  label: "Anonymous · role only", example: "Business Owner, Chennai" },
];

const PERMISSIONS = [
    { key: "Website Only",           label: "Website only" },
    { key: "Website + Social Media", label: "Website + social media" },
    { key: "Anonymous Only",         label: "Anonymous only" },
    { key: "Do Not Publish",         label: "Do not publish — internal only" },
];

const CUSTOMER_TYPES = ["Individual", "Family", "Business", "Advisor"];

const QUESTIONS = [
    { key: "problem_solved", label: "What problem were you trying to solve?" },
    { key: "feedback",       label: "How has VAULT helped you stay organised?" },
    { key: "most_useful",    label: "What feature do you find most useful?" },
    { key: "short_quote",    label: "In one line — what would you tell someone considering VAULT?",
                             required: true, minLen: 8, maxLen: 280 },
];

const initial = {
    customer_name: "", customer_email: "", customer_type: "Individual",
    role: "", company: "", country: "", city_public: "",
    display_format: "first_initial", publication_permission: "Website Only",
    rating: "", short_quote: "", feedback: "", problem_solved: "", most_useful: "",
    would_recommend: null, accuracy_confirmed: false, source: "In-App Feedback",
};

export default function ShareExperience() {
    const [form, setForm] = useState(initial);
    const [submitted, setSubmitted] = useState(null);

    const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async () => {
        if (!form.customer_name.trim()) return toast.error("Please enter your name");
        if (!form.short_quote || form.short_quote.trim().length < 8) return toast.error("Please add a short quote");
        if (!form.accuracy_confirmed) return toast.error("Please confirm the accuracy of your quote");
        try {
            const payload = {
                ...form,
                rating: form.rating ? Number(form.rating) : null,
                customer_email: form.customer_email || null,
            };
            const r = await api.post("/public/testimonials/submit-full", payload);
            setSubmitted(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't submit");
        }
    };

    if (submitted) {
        return (
            <div className="max-w-2xl mx-auto p-8 text-center" data-testid="share-experience-thanks">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-6">
                    <ShieldCheck size={24} className="text-emerald-700" />
                </div>
                <h1 className="font-display text-4xl text-stone-900">Thank you.</h1>
                <p className="text-sm text-stone-600 mt-3">
                    {submitted.status}
                </p>
                <p className="text-[12px] text-stone-500 mt-6">
                    Nothing appears publicly until it has been verified, approved and consented — governance you can rely on.
                </p>
                <Link to="/" className="inline-flex items-center gap-1 mt-8 text-[13px] font-semibold text-stone-700 hover:text-stone-900">
                    <ArrowLeft size={13} /> Back to home
                </Link>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto p-6 md:p-8" data-testid="share-experience">
            <div className="mb-6">
                <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                    <MessageCircle size={12} /> Help · Feedback
                </div>
                <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                    Share your VAULT experience.
                </h1>
                <p className="text-sm text-stone-500 mt-3">
                    Your words help future customers decide whether VAULT is right for them. We publish only your
                    approved wording, and never claim ratings or numbers we can&rsquo;t verify.
                </p>
            </div>

            <section className="space-y-4 bg-white border border-stone-200 rounded-2xl p-6 mb-6" data-testid="share-identity">
                <h2 className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">About you</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Your name
                        <input value={form.customer_name} onChange={e => update("customer_name", e.target.value)}
                               className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                               data-testid="share-name" />
                    </label>
                    <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Email (optional)
                        <input type="email" value={form.customer_email} onChange={e => update("customer_email", e.target.value)}
                               className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                               data-testid="share-email" />
                    </label>
                    <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Customer type
                        <select value={form.customer_type} onChange={e => update("customer_type", e.target.value)}
                                className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                                data-testid="share-customer-type">
                            {CUSTOMER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </label>
                    <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Role (optional)
                        <input value={form.role} onChange={e => update("role", e.target.value)}
                               className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                               placeholder="e.g., Business Owner"
                               data-testid="share-role" />
                    </label>
                    <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Company (optional)
                        <input value={form.company} onChange={e => update("company", e.target.value)}
                               className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                               data-testid="share-company" />
                    </label>
                    <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Country (ISO, optional)
                        <input value={form.country} onChange={e => update("country", e.target.value.toUpperCase())} maxLength={3}
                               className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm uppercase"
                               placeholder="IN / GB / US / SG"
                               data-testid="share-country" />
                    </label>
                    <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">City (optional)
                        <input value={form.city_public} onChange={e => update("city_public", e.target.value)}
                               className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                               data-testid="share-city" />
                    </label>
                    <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Rating (voluntary · 1-5)
                        <input type="number" min="1" max="5" value={form.rating}
                               onChange={e => update("rating", e.target.value)}
                               className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                               data-testid="share-rating" />
                    </label>
                </div>
            </section>

            <section className="space-y-4 bg-white border border-stone-200 rounded-2xl p-6 mb-6" data-testid="share-feedback">
                <h2 className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">Your feedback</h2>
                {QUESTIONS.map(q => (
                    <label key={q.key} className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">
                        {q.label}{q.required && <span className="text-rose-600"> *</span>}
                        {q.key === "short_quote" ? (
                            <textarea value={form[q.key]} onChange={e => update(q.key, e.target.value)}
                                      maxLength={q.maxLen || 400} rows={2}
                                      className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                                      data-testid={`share-${q.key}`} />
                        ) : (
                            <textarea value={form[q.key]} onChange={e => update(q.key, e.target.value)}
                                      rows={2}
                                      className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                                      data-testid={`share-${q.key}`} />
                        )}
                    </label>
                ))}
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5">Would you recommend VAULT?</div>
                    <div className="flex gap-2">
                        {[["yes", "Yes", true], ["no", "No", false], ["unsure", "Prefer not to say", null]].map(([k, label, v]) => (
                            <button key={k} type="button" onClick={() => update("would_recommend", v)}
                                    className={`px-3 py-1.5 rounded-full border text-[12.5px] font-semibold ${form.would_recommend === v ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-700"}`}
                                    data-testid={`share-recommend-${k}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="space-y-4 bg-white border border-stone-200 rounded-2xl p-6 mb-6" data-testid="share-privacy">
                <h2 className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">Display & Consent</h2>
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5">How should your name appear?</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {DISPLAY_FORMATS.map(f => (
                            <label key={f.key}
                                   className={`flex items-start gap-2 border rounded-lg px-3 py-2 cursor-pointer ${form.display_format === f.key ? "border-stone-900 bg-stone-50" : "border-stone-200 bg-white"}`}
                                   data-testid={`share-display-${f.key}`}>
                                <input type="radio" name="display_format" checked={form.display_format === f.key}
                                       onChange={() => update("display_format", f.key)}
                                       className="mt-1" />
                                <span>
                                    <span className="text-[13px] font-semibold text-stone-900 block">{f.label}</span>
                                    <span className="text-[11px] text-stone-500 block">{f.example}</span>
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5">
                        May VAULT use this feedback as a testimonial on its website and marketing channels?
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {PERMISSIONS.map(p => (
                            <label key={p.key}
                                   className={`flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer ${form.publication_permission === p.key ? "border-stone-900 bg-stone-50" : "border-stone-200 bg-white"}`}
                                   data-testid={`share-perm-${p.key.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                                <input type="radio" name="publication_permission" checked={form.publication_permission === p.key}
                                       onChange={() => update("publication_permission", p.key)} />
                                <span className="text-[13px] text-stone-900">{p.label}</span>
                            </label>
                        ))}
                    </div>
                </div>
                <label className="flex items-start gap-2 pt-2 border-t border-stone-100">
                    <input type="checkbox" checked={form.accuracy_confirmed}
                           onChange={e => update("accuracy_confirmed", e.target.checked)}
                           className="mt-1" data-testid="share-accuracy" />
                    <span className="text-[12.5px] text-stone-700">
                        I confirm the accuracy of my quote and that these are my own words.
                    </span>
                </label>
                <div className="flex items-start gap-2 text-[11px] text-stone-500">
                    <Info size={12} className="mt-0.5 shrink-0" />
                    <span>
                        You can withdraw publication permission at any time. Ratings you supply are optional and are
                        only aggregated once we have enough verified ratings; VAULT never claims numbers we can&rsquo;t verify.
                    </span>
                </div>
            </section>

            <div className="flex items-center justify-end gap-2">
                <Link to="/" className="text-[13px] font-semibold text-stone-600 hover:text-stone-900">Cancel</Link>
                <button onClick={submit}
                        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-stone-900 text-white text-[13.5px] font-bold hover:bg-stone-700"
                        data-testid="share-submit">
                    <Send size={13} /> Submit feedback
                </button>
            </div>
        </div>
    );
}
